<script setup lang="ts">
  import TopBar from '@/components/TopBar.vue'
  import { useTemplateValidation } from '@/composables/validation/use-template-validation'
  import { useAddressBookStore } from '@/store/address-book.store'
  import { useEuralStore } from '@/store/eural.store'
  import { useProjectStore } from '@/store/project.store'
  import { useTemplateStore } from '@/store/template.store'
  import { createEmptyShipmentTemplate } from '@/utils/template'
  import {
    Bgb,
    Ecmr,
    DropDown,
    UiButton,
    FormInput,
    type Template,
    isUuid,
    useToastStore,
  } from '@logistiek-zonder-papier/lzp-library'
  import { computed, onMounted, ref } from 'vue'
  import { useRoute, useRouter } from 'vue-router'

  const addressBookStore = useAddressBookStore()
  const euralStore = useEuralStore()
  const projectStore = useProjectStore()
  const templateStore = useTemplateStore()
  const toastStore = useToastStore()
  const route = useRoute()
  const router = useRouter()

  const model = ref<Template>(createEmptyShipmentTemplate())
  const projectPreselected = ref<boolean>(false)

  const validation = useTemplateValidation(model)

  const project = computed({
    get: () =>
      projectStore.projects?.find(project => project.id === model.value?.projectId) ?? null,
    set: project => (model.value.projectId = project?.id),
  })

  onMounted(async () => {
    const projectIdParam = route.params['projectId']
    const projectId = Array.isArray(projectIdParam) ? projectIdParam[0] : projectIdParam
    if (isUuid(projectId)) {
      projectPreselected.value = true
      model.value.projectId = projectId
    }

    const templateIdParam = route.params['templateId']
    const templateId = Array.isArray(templateIdParam) ? templateIdParam[0] : templateIdParam
    if (isUuid(templateId)) {
      model.value = await templateStore.fetch(templateId)
      model.value.type = model.value.bgb ? 'bgb' : 'ecmr'
    }

    await addressBookStore.fetchAddressBooks()
    await euralStore.fetchEuralCodes()
    await projectStore.fetchProjects()
  })

  async function submit() {
    if (!validation.validate()) {
      toastStore.error('Sommige velden ontbreken')
      return
    }

    if (model.value.id) {
      await templateStore.updateTemplate(model.value)
    } else {
      await templateStore.createTemplate(model.value)
    }

    if (projectPreselected.value) {
      await router.push({ name: 'project-details', params: { projectId: model.value.projectId } })
    } else {
      await router.push({ name: 'template-overview' })
    }
  }
</script>

<template>
  <TopBar />
  <div class="container" v-if="model && addressBookStore.addressBookOrganisations">
    <FormInput label="Naam" v-model="model.name" :error="validation.visibleErrors.value['name']" />

    <DropDown
      label="Project"
      v-model="project"
      :disabled="projectPreselected"
      required
      idKey="id"
      :data="projectStore.projects"
      :error="validation.visibleErrors.value['projectId']"
    >
      <template #item="{ item }">
        {{ item.name }}
      </template>
    </DropDown>

    <DropDown
      label="Type"
      :modelValue="{ key: model.type }"
      @update:modelValue="(value: { key: 'bgb' | 'ecmr' }) => (model.type = value.key)"
      :disabled="projectPreselected"
      required
      idKey="key"
      :data="[{ key: 'bgb' }, { key: 'ecmr' }]"
    >
      <template #item="{ item }">
        {{ item.key }}
      </template>
    </DropDown>

    <Bgb
      v-if="model.type === 'bgb'"
      v-model:bgb="model.bgb"
      v-model:shipment="model"
      :addressBook="addressBookStore.addressBookOrganisations"
      :euralCodes="euralStore.euralCodes"
      :errors="validation.visibleErrors.value"
    />

    <Ecmr
      v-if="model.type === 'ecmr'"
      v-model:ecmr="model.ecmr"
      v-model:shipment="model"
      :addressBook="addressBookStore.addressBookOrganisations"
      :errors="validation.visibleErrors.value"
    />

    <div class="button-container">
      <UiButton class="button" label="Opslaan" variant="prominent" @click="submit" />
    </div>
  </div>
</template>

<style lang="css" scoped>
  .container {
    padding: var(--spacing-m) var(--spacing-m);
    background-color: var(--colours-grey-1);
    overflow: auto;
  }

  .button-container {
    display: flex;
    width: 100%;
    flex-direction: row;
    justify-content: end;
    padding: var(--spacing-s);
  }
</style>
